import React, { useCallback, useMemo, useRef, useState } from "react";
import Flx from "../layout/Flx";
import { grey } from "@mui/material/colors";
import SearchInput from "../inputs/SearchInput";
import { Button } from "@mui/material";
import { AddOutlined, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { isEmpty, size } from "lodash";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import { AgGridReact } from "ag-grid-react";
import { formatPercent } from "../../utils/numbers/formatPercent";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesPercent } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesPercent";

const AssociatedBorrowerEntitiesTable = ({
  entities = [],
  onAdd,
  onEdit,
  onDelete,
  onRowClick,
}) => {
  const ref = useRef(null);
  const [selected, setSelected] = useState([]);
  const [quickFilter, setQuickFilter] = useState(null);

  const handleRowSelectionChange = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelected(selectedRows);
  }, []);

  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick) {
        onRowClick(params.data);
      }
    },
    [onRowClick],
  );

  const handleEdit = useCallback(() => {
    if (!isEmpty(selected) && onEdit) {
      onEdit(selected[0]); // Edit first selected
    }
  }, [selected, onEdit]);

  const handleDelete = useCallback(() => {
    if (!isEmpty(selected) && onDelete) {
      onDelete(selected);
    }
  }, [selected, onDelete]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Entity Name",
        field: "entityName",
        flex: 1,
        minWidth: 200,
        sort: "asc",
        // checkboxSelection: true,
        // headerCheckboxSelection: true,
      },
      {
        headerName: "Entity Type",
        field: "entityType",
        width: 120,
        minWidth: 100,
      },
      {
        headerName: "State of Organization",
        field: "entityStateOfOrganization",
        width: 180,
        minWidth: 150,
      },
      {
        headerName: "Organization Date",
        field: "entityOrganizationDate",
        width: 150,
        valueFormatter: (params) => {
          return params.value ? formatDateShort(params.value) : "-";
        },
      },
      {
        headerName: "Borrower Ownership %",
        field: "applicantEntityPercentOwnership",
        width: 180,
        type: "percent",
        valueFormatter: (params) => {
          return params.value ? formatPercent(params.value) : "-";
        },
      },
      {
        headerName: "Entity Members",
        field: "entityMembers",
        width: 140,
        valueFormatter: (params) => {
          const members = params.value || [];
          return size(members) > 0
            ? `${size(members)} member${size(members) !== 1 ? "s" : ""}`
            : "No members";
        },
      },
      {
        headerName: "EIN",
        field: "entityEINNumber",
        width: 120,
        minWidth: 100,
      },
    ],
    [],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesPercent(),
    };
  }, []);

  return (
    <Flx
      column
      sx={{
        p: 0,
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <Flx
        wrap
        fw
        jb
        ac
        gap={2}
        sx={{
          p: 2,
          background: grey[100],
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <SearchInput
          fullWidth
          value={quickFilter}
          sx={{ flexBasis: "400px", flexGrow: 1, background: "#ffffff" }}
          onChange={(e) => setQuickFilter(e)}
        />
        <Flx gap={1}>
          {onEdit && (
            <Button
              onClick={handleEdit}
              variant="outlined"
              size="medium"
              disabled={isEmpty(selected)}
              startIcon={<EditOutlined />}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              size="medium"
              disabled={isEmpty(selected)}
              startIcon={<DeleteOutline />}
            >
              Delete ({size(selected)})
            </Button>
          )}
          {onAdd && (
            <Button
              onClick={onAdd}
              variant="contained"
              size="medium"
              startIcon={<AddOutlined />}
            >
              Add Entity
            </Button>
          )}
        </Flx>
      </Flx>
      <AgGridTableWrapper simpleTable>
        <AgGridReact
          ref={ref}
          rowData={entities}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          onSelectionChanged={handleRowSelectionChange}
          onRowClicked={handleRowClick}
          //   rowSelection={{
          //     mode: "multiRow",
          //   }}
        />
      </AgGridTableWrapper>
    </Flx>
  );
};

export default AssociatedBorrowerEntitiesTable;
